import { Injectable, OnDestroy } from "@angular/core";
import { CallingState, TypeContact, videoConstraints, WebphoneStore } from "./webphone.store";
import { WebphoneQuery } from "./webphone.query";
import {
  Invitation,
  Inviter,
  InviterInviteOptions,
  Registerer,
  Session,
  SessionState,
  UserAgent,
  UserAgentOptions,
  Web
} from "sip.js";
import { IncomingResponse } from "sip.js/lib/core";
import { CallDirection, CallState } from "@etop/models";
import { v4 as uuidv4 } from 'uuid';
import { UtilService } from "apps/core/src/services/util.service";
import { AuthenticateStore, ConfigService } from "@etop/core";
import { CallLogQuery, CallLogService, CallLogStore } from '@etop/state/shop/call-log';
import { ExtensionQuery } from '@etop/state/shop/extension';
import { ShopSettingsService } from "@etop/features/services/shop-settings.service";
import { HotlineQuery } from "@etop/state/shop/hotline";
import { AuthorizationAPI, AuthorizationApi, ContactApi } from "@etop/api";
import { OAFollowersQuery, OAFollowersService } from "@etop/state/zalo/oa-followers";
import { OAConnectionQuery } from "@etop/state/zalo/oa-connection";


export const CALL_FEE_PER_SECOND = 15;

@Injectable({
  providedIn: "root"
})
export class WebphoneService implements OnDestroy {

  userAgent: UserAgent;
  registerer: Registerer;

  incomingInvitation: Invitation;
  outgoingInviter: Inviter;

  session: Session;

  telecomBalance: number;
  hotlines = [];
  hotline_id = '';

  private observer: MutationObserver = null;
  private observerTimer: any = null;
  _userAgentOptions: UserAgentOptions = null;

  constructor(
    private store: WebphoneStore,
    private query: WebphoneQuery,
    private util: UtilService,
    private auth: AuthenticateStore,
    private callLogService: CallLogService,
    private callLogStore: CallLogStore,
    private callLogQuery: CallLogQuery,
    private extensionQuery: ExtensionQuery,
    private shopSettingsService: ShopSettingsService,
    private hotlineQuery: HotlineQuery,
    private contactApi: ContactApi,
    private authorizationApi: AuthorizationApi,
    private oaFollowerService: OAFollowersService,
    private oaConnectQuery: OAConnectionQuery,
    private oaFollowersQuery: OAFollowersQuery,
    private config: ConfigService,
  ) {
    // Khi có mạng trở lại
    window.addEventListener("online", async () => {
      if (this.userAgent && !this.userAgent.isConnected() && this._userAgentOptions) {
        await this.registerPortsip(this._userAgentOptions);
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        setTimeout(async () => {
          if (this.userAgent && !this.userAgent.isConnected()) {
            await this.registerPortsip(this._userAgentOptions);
          }
        }, 1500);
      }
    });
  }

  requestNotificationPermission() {
    if (!("Notification" in window)) {
      console.error("Trình duyệt không hỗ trợ Notification API.");
      return;
    }

    if (Notification.permission === "granted") {
      console.log("Đã có quyền gửi notification.");
      return;
    }

    if (Notification.permission === "denied") {
      console.error("Người dùng từ chối quyền notification.");
      return;
    }

    // Yêu cầu quyền
    Notification.requestPermission().then(permission => {
      if (permission === "granted") {
        console.log("Đã cấp quyền notification.");
      }
    });
  }

  async showIncomingCallNotification(callerName, callerNumber) {

    if (!("Notification" in window)) {
      console.error("Trình duyệt không hỗ trợ Notification API");
      return null;
    }

    // 2. Quan trọng nhất: tự động xin quyền nếu chưa có
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        console.warn("Người dùng không cho phép notification:", permission);
        alert("Vui lòng cho phép thông báo để nhận cuộc gọi khi tab không active!");
        return null;
      }
    }

    // 3. Nếu bị chặn hoàn toàn
    if (Notification.permission === "denied") {
      console.warn("Notification bị chặn bởi người dùng");
      alert("Thông báo bị chặn! Vào chrome://settings/content/notifications để bật lại.");
      return null;
    }

    // Tạo notification
    const notification = new Notification("Cuộc gọi đến!", {
      body: `${callerName ?? ""}\n${callerNumber}`,
      icon: this.config.get('favicon'),
      badge: this.config.get('favicon'),
      tag: "sip-incoming-call-" + Date.now(),
      requireInteraction: true,
      silent: false,
    });

    // Action khi click notification (ví dụ: focus tab hoặc answer call)
    notification.onclick = () => {
      window.focus(); // Focus tab
      // Có thể trigger answer: session.answer();
      notification.close();
    };

    // Auto close sau 10s nếu không interact
    setTimeout(() => {
      notification.close();
    }, 10000);
  }

  getInstanceId(): string {
    const _instance_name = 'instance_id_' + this.auth.snapshot.shop.id
    let instance_id = localStorage.getItem(_instance_name);
    if (!instance_id) {
      instance_id = uuidv4();
      localStorage.setItem(_instance_name, instance_id);
    }
    return instance_id;
  }

  async login(username: string, password: string, domain: string, sip_server: string) {
    if (!username || !password || !domain) {
      return;
    }
    this.requestNotificationPermission();
    const isPortsipRegistered$ = this.query.portsipRegisteredListener;
    try {
      const _uri = UserAgent.makeURI(`sip:${username}@${domain}`);
      if (!_uri) {
        return;
      }

      const userAgentOptions: UserAgentOptions = {
        uri: _uri,
        authorizationUsername: username,
        authorizationPassword: password,
        transportOptions: {
          server: sip_server,
          keepAliveInterval: 30,
          keepAliveDebounce: 5,
          connectionTimeout: 10,
          expires: 600 // seconds
        },
        userAgentString: `EtelecomCS-WebPhone/1.0.0`,
        logLevel: "error",
        displayName: username,
        delegate: {
          onInvite: (invitation: Invitation) => {
            this.receiveCall(invitation);
          },
          onDisconnect: async (error: Error) => {
            if (error) {
              this.store.update({ onWebSocketClose: true });
            }
          },
          onConnect: () => {
            this.store.update({ onWebSocketClose: false });
          }
        }
      };

      this._userAgentOptions = userAgentOptions; // Lưu lại để retry khi resume

      if (!this.query.getValue().isPortsipRegistered) {
        await this.registerPortsip(userAgentOptions);
      } else {
        isPortsipRegistered$.subscribe(isPortsipRegistered => {
          if (!isPortsipRegistered) {
            this.registerPortsip(userAgentOptions);
          }
        });
      }
    } catch (e) {
      console.error('ERROR in login', e);
    }
  }

  async registerPortsip(_userAgentOptions: UserAgentOptions) {
    const instanceId = this.getInstanceId();
    const userAgentOptionsWithInstanceId = {
      ..._userAgentOptions,
      instanceId
    } as UserAgentOptions & { instanceId?: string };

    this.userAgent = new UserAgent(userAgentOptionsWithInstanceId);
    this.registerer = new Registerer(this.userAgent, { expires: 300, instanceId });

    await this.userAgent.start();

    await this.registerer?.register({
      requestDelegate: {
        onAccept: _ => {
          this.store.setPortsipErrorCode("");
        },
        onReject: (resp) => {
          if (resp.message.statusCode) {
            this.store.setPortsipErrorCode(resp.message.statusCode.toString());
            this.store.update({ onWebSocketClose: true });
          }
        }
      }
    });

    this.store.update({ isPortsipRegistering: false });
    this.store.update({ isPortsipRegistered: true });
  }

  async logout() {
    try {
      if (this.query.portsipRegistered) {
        await this.registerer?.unregister();
        await this.userAgent?.stop();
        this.store.setPortsipErrorCode("");
      }
    } catch (e) {
      console.error('ERROR in logout', e);
    }
    this.store.update({ isPortsipRegistered: false });
  }

  async makeCall(phoneNumber: string, isVideoCall: boolean = false) {
    if (!this.userAgent.isConnected()) {
      toastr.error('Không thể kết nối tổng đài, vui lòng kiểm tra mạng hoặc làm mới lại (F5) trình duyệt');
      return;
    }
    const now = new Date();
    // if (this.query.getValue().callState != 0) {
    //   throw new Error('Bạn đang trong cuộc gọi, không thể thực hiện một cuộc gọi khác.');
    // }
    try {
      await this._checkTelecomBalance();

      phoneNumber = this.util.replacePrefixPhoneNumber(phoneNumber);

      const isPortsipRegistered = this.query.portsipRegistered;
      const portsipErrorCode = this.query.portsipErrorCode;
      if (portsipErrorCode) {
        return;
      }

      if (isPortsipRegistered) {
        this.store.update({
          callState: CallingState.callingOut,
          callingPhoneNumber: phoneNumber,
          isVideoCall: isVideoCall
        });

        if (!this._getFollowerOA(phoneNumber)) {
          this._getPhoneContact(phoneNumber);
          this._getRelationshipContact(phoneNumber);
        }

        // Send an outgoing INVITE request
        const target = UserAgent.makeURI(`sip:${phoneNumber}@${this.auth.snapshot.extension?.tenant_domain}`);
        if (!target) {
          return;
        }

        const inviter = new Inviter(this.userAgent, target, {
          earlyMedia: true
        });

        this.outgoingInviter = inviter;
        this.session = inviter;
        this._sessionStateListener();

        const constraints: MediaStreamConstraints = {
          video: isVideoCall ? videoConstraints : false,
          audio: true
        };

        if (isVideoCall) {
          navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
            this.store.setLocalVideo(stream);
          });
        }

        // Send the INVITE request
        const _inviterInviteOptions: InviterInviteOptions = {
          requestDelegate: {
            onProgress: (response: IncomingResponse) => {
              if (response.message.data.replace(/[\r\n" ]*/g, "").includes("application/sdp")) {
                const sessionDescriptionHandler = this.session.sessionDescriptionHandler;
                if (sessionDescriptionHandler && sessionDescriptionHandler instanceof Web.SessionDescriptionHandler) {
                  const srcObject = sessionDescriptionHandler.remoteMediaStream;

                  const remoteAudio = document.getElementById("remoteAudio") as HTMLAudioElement;
                  remoteAudio.autoplay = true;
                  remoteAudio.srcObject = srcObject;
                  remoteAudio.play().catch(err => console.error("ERROR in Streaming Remote Audio", err));

                  this.store.update({
                    localAudioSrc: ""
                  });
                }
              } else {
                this.store.update({
                  localAudioSrc: "https://etelecom.s3south.storage.com.vn/ringtones/ring_back_tone.mp3"
                });
              }
              if (response.message.headers['X-Session-Id']?.length) {
                this.callLogStore.setNewCallLog({
                  external_session_id: response.message.headers['X-Session-Id'][0]?.raw
                });
              }
            },
            onAccept: (response: IncomingResponse) => {
              this.callLogStore.setNewCallLog({
                call_state: CallState.answered,
              });
              this._processCalls();
            },
            onReject: (response: IncomingResponse) => {
              this.store.update({
                statusCodeEndCall: response.message.statusCode,
              });
            }
          },
          sessionDescriptionHandlerOptions: { constraints }
        };

        await this.outgoingInviter.invite(_inviterInviteOptions);

        this.callLogStore.setNewCallLog({
          call_state: CallState.not_answered,
          callee: phoneNumber,
          caller: this.auth.snapshot?.extension?.extension_number,
          direction: this.extensionQuery.isInternal(phoneNumber) ? CallDirection.ext : CallDirection.out,
          extension_id: this.auth.snapshot?.extension.id,
          external_session_id: "",
          started_at: new Date(Date.now()),
        });

      }

    } catch (e) {
      this._endCalls();
      throw e;
    }
  }

  receiveCall(invitation: Invitation) {
    this.incomingInvitation = invitation;
    this.session = invitation;
    this._sessionStateListener();

    const _state = this.query.getValue().callState;
    if (_state != CallingState.callProcessing) {
      const phoneNumber = this.incomingInvitation.request.from.uri?.user;

      this.callLogStore.setNewCallLog({
        call_state: CallState.not_answered,
        callee: this.auth.snapshot.extension?.extension_number,
        caller: phoneNumber,
        direction: this.extensionQuery.isInternal(phoneNumber) ? CallDirection.ext : CallDirection.in,
        extension_id: this.auth.snapshot.extension.id,
        external_session_id: "",
        started_at: new Date(Date.now()),
      });





      if (this.incomingInvitation.request.headers['X-Session-Id']?.length) {
        this.callLogStore.setNewCallLog({
          external_session_id: this.incomingInvitation.request.headers['X-Session-Id'][0]?.raw
        });
      }
      let displayName = this.incomingInvitation?.request?.from?.displayName;
      if (displayName && phoneNumber != displayName) {
        this.store.update({
          callingPhoneName: displayName
        });
      } else {
        this._getPhoneContact(phoneNumber);
        this._getRelationshipContact(phoneNumber);
      }
      if (document.visibilityState != "visible") {
        setTimeout(() => {
          this.showIncomingCallNotification(this.store.getValue().callingPhoneName, phoneNumber)
        }, 500);
      }
      

      this.store.update({
        onSwitchingShop: false,
        callState: CallingState.callingIn,
        localAudioSrc: "https://etelecom.s3south.storage.com.vn/ringtones/ringtone.mp3",
        callingPhoneNumber: phoneNumber,
        isVideoCall: this.incomingInvitation.body.includes("label:video_label")
      });
    }
  }

  async answer() {
    try {
      const constraints: MediaStreamConstraints = {
        video: this.query.isVideoCall ? videoConstraints : false,
        audio: true
      };

      if (this.query.isVideoCall) {
        navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
          this.store.setLocalVideo(stream);
        });
      }

      await this.incomingInvitation.accept({
        sessionDescriptionHandlerOptions: { constraints }
      });

      this.callLogStore.setNewCallLog({
        call_state: CallState.answered
      });

      this._processCalls();
    } catch (e) {
      console.error('ERROR in answer', e);
    }
  }

  async reject() {
    try {
      this._endCalls();
      await this.incomingInvitation.reject({ statusCode: 486 });
      // await this.callSessionApi.destroyCallSession({
      //   xSessionId: this.incomingInvitation.request.headers['X-Session-Id'][0]?.raw,
      //   extensionId: this.authQuery.user.extension.id
      // });
    } catch (e) {
      console.error('ERROR in WebphoneService.reject', e);
    }
  }

  async hangup() {
    try {
      this._endCalls();

      if (this.session.state == SessionState.Initial || this.session.state == SessionState.Establishing) {
        await this.outgoingInviter.cancel();
      } else {
        await this.session?.bye();
      }

    } catch (e) {
      console.error('ERROR in hangup', e);
    }
  }

  holdToggle(held: boolean) {
    try {
      const _holdOptions: any = {
        hold: held
      };

      this.session.sessionDescriptionHandlerOptionsReInvite = _holdOptions;

      this.session.invite().then();

      this.store.update({
        callState: held ? CallingState.callHolding : CallingState.callProcessing
      });
    } catch (e) {
      console.error('ERROR in holdToggle', e);
    }
  }

  setLocalAudioMuted(muted = true) {
    const sdh = this.session.sessionDescriptionHandler as Web.SessionDescriptionHandler;
    const pc = sdh.peerConnection;
    if (pc) {
      pc.getSenders().forEach(sender => {
        if (sender.track?.kind === 'audio') {
          sender.track.enabled = !muted;
        }
      });
    }
  }

  async sendDtmf(tone: string) {
    try {

      const dtmf = tone;
      const duration = 160;
      const options = {
        requestOptions: {
          body: {
            contentDisposition: "render",
            contentType: "application/dtmf-relay",
            content: `Signal=${dtmf}\r\nDuration=${duration}`
          },
        },
      };

      await this.session.info(options);

    } catch (e) {
      console.error('ERROR in sendDtmf', e);
    }
  }

  async confirmRefer(referNumber: string) {
    try {
      const user = this.auth.snapshot.user;
      const targetURI = UserAgent.makeURI(`sip:${referNumber}@${this.auth.snapshot.extension?.tenant_domain}`);
      await this.session.refer(targetURI, {
        onNotify: (_ => {
          this.session.bye();
        })
      });
    } catch (e) {
      console.error('ERROR in confirmRefer', e);
    }
  }

  private _processCalls() {
    this.store.update({
      callState: CallingState.callProcessing,
      localAudioSrc: ""
    });

    const sessionDescriptionHandler = this.session.sessionDescriptionHandler;
    if (sessionDescriptionHandler && sessionDescriptionHandler instanceof Web.SessionDescriptionHandler) {
      const srcObject = sessionDescriptionHandler.remoteMediaStream;

      if (this.query.isVideoCall) {
        this.store.setRemoteVideo(srcObject);
      } else {
        const remoteAudio = document.getElementById("remoteAudio") as HTMLAudioElement;
        remoteAudio.autoplay = true;
        remoteAudio.srcObject = srcObject;
        remoteAudio.play().catch(err => console.error("ERROR in Streaming Remote Audio", err));
      }

    }
  }

  private _endCalls() {
    this.store.update({
      callState: CallingState.none,
      localAudioSrc: "https://etelecom.s3south.storage.com.vn/ringtones/endcall_sound.wav",
      callingPhoneName: null,
      isVideoCall: false
    });

    this.callLogStore.setNewCallLog(
      {
        ended_at: new Date(Date.now())
      }
    );

    this.store.clearVideos(this.query.currentLocalVideoSrc, this.query.currentRemoteVideoSrc);

    setTimeout(() => {
      this.store.update({
        statusCodeEndCall: null,
        localAudioSrc: ""
      });
    }, 1500);
  }

  private _sessionStateListener() {
    this.session.stateChange.addListener((state: SessionState) => {
      switch (state) {
        case SessionState.Terminated:
          this._endCalls();
          this.callLogService.createCallLog(this.callLogQuery.newCallLog).finally(() => {
            this.callLogStore.setNewCallLog(null);
          });
          break;
        default:
          break;
      }
    });
  }

  private async _checkTelecomBalance() {
    const isFreeCharge = this.getIsFreeCharge();
    if (!isFreeCharge) {
      this.telecomBalance = await this.shopSettingsService.getTelecomBalance('telecom');
      if (this.telecomBalance / CALL_FEE_PER_SECOND < 20) {
        throw new Error("Không đủ số dư để thực hiện cuộc gọi");
      }
    }
  }

  getIsFreeCharge() {
    const hotlineId = this.auth.snapshot?.extension?.hotline_id;
    return this.hotlineQuery.getIsFreeCharge(hotlineId);
  }

  checkHotline() {
    this.hotlines = this.hotlineQuery.getAll();
    this.hotline_id = this.auth.snapshot.extension?.hotline_id;
    return this.hotlines.find(hotline => hotline.network == 'unknown' && hotline.hotline.length > 12 && hotline?.id == this.hotline_id);
  }

  private _getPhoneContact(phoneNumber: string) {
    this.contactApi.getContacts({
      filter: { phone: phoneNumber },
      paging: { limit: 1000, after: "." }
    }).then(res => {
      if (res?.contacts.length > 0) {
        const contact = res?.contacts[0];
        this.store.update({
          callingPhoneName: contact.full_name,
          typeContact: TypeContact.general
        });
        this.callLogStore.setNewCallLog({
          contact_id: contact.id
        });
      } else {
        this.callLogStore.setNewCallLog({
          contact_id: null
        });
      }
    });
  }

  private _getRelationshipContact(phoneNumber: string) {
    const request: AuthorizationAPI.GetAccountUserRequest = {
      paging: {
        limit: 20,
        after: "."
      },
      filter: {
        extension_number: phoneNumber
      }
    };
    this.authorizationApi.getAccountUsers(request).then(res => {
      if (res.account_users[0]) {
        this.store.update({
          callingPhoneName: res.account_users[0].full_name,
          typeContact: TypeContact.internal
        });
      }
    });
  }

  private _getFollowerOA(id: string) {
    const shopOA = this.oaConnectQuery.getAll()[0];
    this.oaFollowerService.setFilter({ zl_user_ids: [id], oa_id: shopOA?.oa_id });
    this.oaFollowerService.setPaging({ limit: 100, after: '.' });
    this.oaFollowerService.getListFollowers().then(_ => {
      let res = this.oaFollowersQuery.getAll();
      if (res?.length) {
        this.store.update({
          callingPhoneName: res[0].full_name,
        });
        return true;
      } else {
        return false;
      }
    });
    return false;
  }

  updateCallFunc(enable: boolean) {
    this.store.update({enableCallFunc: enable})
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.observerTimer) {
      clearTimeout(this.observerTimer);
      this.observerTimer = null;
    }
  }
}
